import React, { useEffect, useState, useCallback } from "react";
import { useLocation } from "react-router-dom";
import { clientService } from "../../../services";
import { Col } from "reactstrap";

import backgroundImage from "../../../assets/images/backgroundImage.png"; // Update with your background image path
import signBanner from "../../../assets/images/emailVerified.png";
import barcode from "../../../assets/images/barcode.png";
import googleButton from "../../../assets/images/blackGoogleButton.png";
import appleButton from "../../../assets/images/blackAppleButton.png";
import successIcon from "../../../assets/images/successIcon.png";
import errorIcon from "../../../assets/images/errorIcon.png"; // Update with your error message image path

import "./EmailVerification.css"; // Assuming you have a CSS file for styling

/**
 * Deduplicate verification PATCH per token for the SPA session.
 * React 18 Strict Mode remounts this component; a ref resets, but the map does not,
 * so we do not fire a second PATCH that often returns "already verified" as an error.
 */
const leadVerifyPromiseByToken = new Map();
/** Strict Mode remounts reset component refs; this does not. */
const verifyEffectStartedForUrl = new Set();

function verifyLeadOnce(token, verifyFn) {
  const key = String(token).trim();
  if (leadVerifyPromiseByToken.has(key)) {
    if (process.env.NODE_ENV === "development") {
      console.info("[EmailVerification] Reusing in-flight / cached verify for token hash", key.slice(0, 8));
    }
    return leadVerifyPromiseByToken.get(key);
  }
  const p = verifyFn()
    .then((res) => res)
    .catch((err) => {
      leadVerifyPromiseByToken.delete(key);
      throw err;
    });
  leadVerifyPromiseByToken.set(key, p);
  return p;
}

function isEmailVerificationRoute(pathname) {
  const pub = (process.env.PUBLIC_URL || "").replace(/\/$/, "");
  const normalized = pathname.replace(/\/+$/, "") || "/";
  const expected = `${pub}/emailVerified`.replace(/\/+/g, "/");
  return normalized === expected;
}

function extractLeadUpdateMessage(payload) {
  if (!payload) return "";
  const { data, result } = payload;
  if (typeof data?.message === "string" && data.message) return data.message;
  if (typeof result?.message === "string" && result.message) return result.message;
  if (typeof result === "string" && result) return result;
  if (data?.result && typeof data.result === "object" && typeof data.result.message === "string") {
    return data.result.message;
  }
  return "";
}

function looksLikeAlreadyVerifiedMessage(message) {
  const m = String(message || "").toLowerCase();
  return (
    m.includes("already verified") ||
    m.includes("already been verified") ||
    m.includes("email is already") ||
    m.includes("already confirm")
  );
}

const EmailVerification = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isLinkExpired, setIsLinkExpired] = useState(false);
  const [responseMessage, setResponseMessage] = useState({
    showBtn: true,
    message: "",
  });

  const { search, pathname } = useLocation();

  const getTokenFromSearch = useCallback(() => {
    const qs = search.startsWith("?") ? search.slice(1) : search;
    return new URLSearchParams(qs).get("token");
  }, [search]);

  const isValidPath = useCallback(() => {
    const isEmailVerifiedPath = isEmailVerificationRoute(pathname);
    const token = getTokenFromSearch();
    return isEmailVerifiedPath && Boolean(token && token.trim());
  }, [pathname, getTokenFromSearch]);

  const applySuccessPayload = useCallback((httpStatus, message) => {
    if (process.env.NODE_ENV === "development") {
      console.info("[EmailVerification] applySuccessPayload", { httpStatus, messagePreview: String(message).slice(0, 80) });
    }
    if (httpStatus === 201) {
      setIsLinkExpired(true);
      setResponseMessage({
        showBtn: false,
        message: message || "",
      });
    } else if (httpStatus === 202) {
      setIsLinkExpired(false);
      setResponseMessage({
        showBtn: false,
        message: message || "",
      });
    } else {
      setIsLinkExpired(false);
      setResponseMessage({
        showBtn: true,
        message: message || "",
      });
    }
  }, []);

  const handleUpdate = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    const tokenFromQuery = getTokenFromSearch();

    if (process.env.NODE_ENV === "development") {
      console.info("[EmailVerification] handleUpdate start", {
        pathname,
        tokenPresent: Boolean(tokenFromQuery),
        tokenLength: tokenFromQuery ? String(tokenFromQuery).length : 0,
      });
    }

    try {
      const postClient = await verifyLeadOnce(tokenFromQuery, () =>
        clientService.updateLeadInfo(tokenFromQuery)
      );

      const httpStatus = postClient?.status;
      const message = extractLeadUpdateMessage(postClient);

      if (process.env.NODE_ENV === "development") {
        console.info("[EmailVerification] updateLeadInfo OK", {
          httpStatus,
          messagePreview: String(message).slice(0, 120),
          rawKeys: postClient ? Object.keys(postClient) : [],
        });
      }

      if (httpStatus === 200 && looksLikeAlreadyVerifiedMessage(message)) {
        setIsLinkExpired(false);
        setResponseMessage({
          showBtn: false,
          message: message || "Your email is already verified.",
        });
        return;
      }

      applySuccessPayload(httpStatus, message);
    } catch (err) {
      const status = err.response?.status;
      const serverMessage =
        err.response?.data?.message ||
        err.response?.data?.error ||
        err.message;
      const msg = serverMessage || "";

      if (process.env.NODE_ENV === "development") {
        console.warn("[EmailVerification] updateLeadInfo error", {
          status,
          message: msg,
          data: err.response?.data,
        });
      }

      const treatAsAlreadyVerified =
        status === 202 ||
        status === 409 ||
        looksLikeAlreadyVerifiedMessage(msg);

      if (treatAsAlreadyVerified) {
        setIsLinkExpired(false);
        setResponseMessage({
          showBtn: false,
          message: msg || "Your email is already verified.",
        });
        return;
      }

      setError(
        msg || "There was an error verifying your email. Please try again later."
      );
    } finally {
      setIsLoading(false);
    }
  }, [getTokenFromSearch, pathname, applySuccessPayload]);

  useEffect(() => {
    if (!isValidPath()) {
      return;
    }
    const urlKey = `${pathname}${search}`;
    if (verifyEffectStartedForUrl.has(urlKey)) {
      return;
    }
    verifyEffectStartedForUrl.add(urlKey);
    handleUpdate();
  }, [isValidPath, handleUpdate, pathname, search]);

  if (!isValidPath()) {
    return (
      <div
        style={{
          backgroundImage: `url(${backgroundImage})`, // Update with your background image path
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
          height: "100vh",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <div className="sideContentWrapper">
          <Col md={6}>
            <h2 style={{ color: "red" }}>Page Not Found</h2>
            <p>
              The page you are looking for does not exist or the link is
              invalid.
            </p>
          </Col>
          {/* RIGHT STICKY IMAGE COLUMN */}
          <Col md={6} className="position-relative p-0 d-none d-sm-block">
            <div className="sideImageWrapper">
              <img
                src={signBanner}
                alt="godoBanner"
                style={{
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                }}
              />
              <Col md={8} className="emailHeadingContent">
                <div className="bannerHeadingEmailVerified text-black px-4">
                  <h1>Join thousands of traders on the the GODO App!</h1>
                  <p>
                    Trader 4000+ assets, invest in 3000+ stocks with 0%
                    commission, Autocopy top traders, and access powerful tool
                    and eduction - all from one easy-to-use app.
                  </p>
                </div>
              </Col>
              <Col md={4} className="emailLinkWrapper">
                <div className="linkWrapper d-flex flex-column gap-3 align-items-center">
                  <a href="https://play.google.com/store/apps/details?id=com.godocp.godo">
                    <img src={googleButton} alt="googleButton" />
                  </a>
                  <a href="https://apps.apple.com/ae/app/godo-app-start-trading-now/id6742449171">
                    <img src={appleButton} alt="appleButton" />
                  </a>
                </div>
                <img
                  className="barcodeEmailVerified"
                  src={barcode}
                  alt="barcode"
                />
              </Col>
            </div>
          </Col>
        </div>
      </div>
    );
  }

  return (
    <div
      style={{
        backgroundImage: `url(${backgroundImage})`, // Update with your background image path
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
        height: "100vh",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
      }}
    >
      <div className="sideContentSuccessWrapper">
        <Col md={6} className="sideContinerHeight">
          {/* Logo at the top */}
          <div className="logoImage">
            <img
              src="https://www.godocm.com/wp-content/uploads/2025/05/cropped-GoDo-Logo-7-1.png" // Update with your logo path
              alt="Company Logo"
            />
          </div>

          {isLoading ? (
            <div className="responseMessage" style={{ fontSize: "16px", marginTop: '30px', color: "#555", height: "400px", display: "flex", flexDirection: 'column', justifyContent: "center", alignItems: "center" }}>
              <div className="loader"></div> {/* The loader */}
            </div>
          ) : error ? (
            <div className="responseMessage" style={{ fontSize: "16px", marginTop: '30px', color: "red", height: "400px", display: "flex", flexDirection: 'column', justifyContent: "center", alignItems: "center" }}>
              <img src={errorIcon} alt="errorIcon" width={"80px"} />
              <p>{error}</p>
            </div>
          ) : isLinkExpired ? (
            <div className="responseMessage" style={{ textAlign: "center", marginTop: '30px', height: "400px", display: "flex", flexDirection: 'column', justifyContent: "center", alignItems: "center" }}>
              <p style={{ fontSize: "16px", color: "red" }}>
                {responseMessage.message}
              </p>
            </div>
          ) : (
            <div
              className="responseMessage"
              style={{
                fontSize: "16px",
                color: "#555",
                textAlign: "center",
              }}
            >
              <img src={successIcon} alt="succcessIcon" width={"80px"} />
              <p className="mt-5">{responseMessage.message}</p>
            </div>
          )}

          {/* Conditionally render the Login Button or Expired message */}
          {!isLinkExpired &&
            !isLoading &&
            !error &&
            responseMessage.showBtn && (
              <div className="portalButton" style={{ textAlign: "center" }}>
                <a
                  href="https://login.godocm.com/login/" // Update with your portal URL
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    backgroundColor: "#094D96",
                    color: "#fff",
                    textDecoration: "none",
                    borderRadius: "6px",
                    fontSize: "16px",
                    fontWeight: "bold",
                  }}
                >
                  Login to Client Portal
                </a>
              </div>
            )}
        </Col>
        {/* RIGHT STICKY IMAGE COLUMN */}
        <Col md={6} className="position-relative p-0 d-none d-sm-block">
          <div className="sideImageWrapper">
            <img
              src={signBanner}
              alt="godoBanner"
              style={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
              }}
            />
            <Col md={8} className="emailHeadingContent">
              <div className="bannerHeadingEmailVerified text-black px-4">
                <h1>Join thousands of traders on the the GODO App!</h1>
                <p>
                  Trader 4000+ assets, invest in 3000+ stocks with 0%
                  commission, Autocopy top traders, and access powerful tool and
                  eduction - all from one easy-to-use app.
                </p>
              </div>
            </Col>
            <Col md={4} className="emailLinkWrapper">
              <div className="linkWrapper d-flex flex-column gap-3 align-items-center">
                <a href="https://play.google.com/store/apps/details?id=com.godocp.godo">
                  <img src={googleButton} alt="googleButton" />
                </a>
                <a href="https://apps.apple.com/ae/app/godo-app-start-trading-now/id6742449171">
                  <img src={appleButton} alt="appleButton" />
                </a>
              </div>
              <img
                className="barcodeEmailVerified"
                src={barcode}
                alt="barcode"
              />
            </Col>
          </div>
        </Col>
      </div>
    </div>
  );
};

export default EmailVerification;
